import React, { useState, useEffect, useCallback } from 'react';
import { airtableService } from '../services/airtable';

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 Days' },
  { id: '30d', label: 'Last 30 Days' },
  { id: 'custom', label: 'Custom' },
];

function getPresetRange(id) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (id === 'today') return { start: startOfToday, end: now };
  if (id === '7d') return { start: new Date(startOfToday - 6 * 864e5), end: now };
  if (id === '30d') return { start: new Date(startOfToday - 29 * 864e5), end: now };
  return null;
}

const Reports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [preset, setPreset] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [loading, setLoading] = useState(true);

  const [allVisitors, setAllVisitors] = useState([]);
  const [allIncidents, setAllIncidents] = useState([]);
  const [allChecks, setAllChecks] = useState([]);
  const [activeVisitorCount, setActiveVisitorCount] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    const [visitors, incidents, checks, active] = await Promise.all([
      airtableService.getAllVisitors(),
      airtableService.getIncidents(),
      airtableService.getPerimeterChecks(),
      airtableService.getActiveVisitors(),
    ]);
    setAllVisitors(visitors);
    setAllIncidents(incidents);
    setAllChecks(checks);
    setActiveVisitorCount(active.length);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Date range helpers ────────────────────────────────────────────────────────

  const { start: rangeStart, end: rangeEnd } = (() => {
    if (preset !== 'custom') return getPresetRange(preset);
    if (customStart && customEnd) {
      return {
        start: new Date(customStart),
        end: new Date(new Date(customEnd).setHours(23, 59, 59, 999)),
      };
    }
    return getPresetRange('today');
  })();

  const inRange = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d >= rangeStart && d <= rangeEnd;
  };

  const filteredVisitors = allVisitors.filter(v => inRange(v.fields['Check In Time']));
  const filteredIncidents = allIncidents.filter(i => inRange(i.fields['Date/Time']));
  const filteredChecks = allChecks.filter(c => inRange(c.fields['Check Time']));

  // ── Formatting ────────────────────────────────────────────────────────────────

  const formatDateTime = (str) => {
    if (!str) return '—';
    try {
      return new Date(str).toLocaleString('en-US', {
        timeZone: 'America/New_York', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return '—'; }
  };

  const StatCard = ({ title, value, icon, color, subtext }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold" style={{ color }}>{value}</p>
          {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
        </div>
        <div className="text-4xl" style={{ color }}>{icon}</div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 text-center py-12">
        <div className="text-lg text-gray-600">Loading dashboard data…</div>
      </div>
    );
  }

  const rangeLabel = preset !== 'custom'
    ? PRESETS.find(p => p.id === preset)?.label
    : (customStart && customEnd ? `${customStart} – ${customEnd}` : 'Custom');

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-1">Security Dashboard</h1>
          <p className="text-gray-500 text-sm">Showing: {rangeLabel}</p>
        </div>
        <button onClick={loadData} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm">
          Refresh Data
        </button>
      </div>

      {/* Date range filter strip */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-center">
        {PRESETS.map(p => (
          <button
            key={p.id}
            onClick={() => setPreset(p.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              preset === p.id
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex gap-2 items-center ml-2">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title="Visitors" value={filteredVisitors.length} icon="👥" color="#3B82F6"
          subtext={`${activeVisitorCount} currently on campus`} />
        <StatCard title="Incidents" value={filteredIncidents.length} icon="⚠️" color="#EF4444"
          subtext={rangeLabel} />
        <StatCard title="Perimeter Checks" value={filteredChecks.length} icon="🔍" color="#10B981"
          subtext={rangeLabel} />
        <StatCard title="Active Visitors" value={activeVisitorCount} icon="🏢" color="#F59E0B"
          subtext="Currently on campus" />
      </div>

      {/* Sub-tab navigation */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="flex border-b border-gray-200">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'visitors', label: 'Visitors' },
            { id: 'incidents', label: 'Incidents' },
            { id: 'perimeter', label: 'Perimeter' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-6 py-4 font-medium text-sm ${
                activeTab === t.id
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-lg shadow-sm p-6">

        {activeTab === 'overview' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Overview — {rangeLabel}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3 text-blue-600">Visitor Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Check-ins:</span><span className="font-medium">{filteredVisitors.length}</span></div>
                  <div className="flex justify-between"><span>Still on Campus:</span><span className="font-medium text-orange-600">{activeVisitorCount}</span></div>
                  <div className="flex justify-between"><span>Checked Out:</span>
                    <span className="font-medium">{filteredVisitors.filter(v => v.fields.Status === 'Checked Out').length}</span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-3 text-green-600">Security Activity</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span>Perimeter Checks:</span><span className="font-medium">{filteredChecks.length}</span></div>
                  <div className="flex justify-between"><span>Issues Found:</span>
                    <span className="font-medium text-orange-600">{filteredChecks.filter(c => c.fields['Completion Status'] === 'Issue Found').length}</span>
                  </div>
                  <div className="flex justify-between"><span>Incidents:</span><span className="font-medium text-red-600">{filteredIncidents.length}</span></div>
                  <div className="flex justify-between"><span>High Priority:</span>
                    <span className="font-medium text-red-600">{filteredIncidents.filter(i => i.fields.Priority === 'High').length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'visitors' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Visitors — {rangeLabel}</h2>
            {filteredVisitors.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No visitors in this date range.</p>
            ) : (
              <div className="space-y-3">
                {filteredVisitors.slice(0, 50).map(v => (
                  <div key={v.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className="font-semibold text-lg">{v.fields.Name}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        v.fields.Status === 'Checked In' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>{v.fields.Status}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>In: {formatDateTime(v.fields['Check In Time'])}</div>
                      {v.fields['Check Out Time'] && <div>Out: {formatDateTime(v.fields['Check Out Time'])}</div>}
                      <div>Reason: {v.fields['Reason for Visit']}</div>
                      <div>By: {v.fields.User || '—'}</div>
                    </div>
                  </div>
                ))}
                {filteredVisitors.length > 50 && (
                  <p className="text-sm text-gray-400 text-center">Showing 50 of {filteredVisitors.length} records</p>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'incidents' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Incidents — {rangeLabel}</h2>
            {filteredIncidents.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No incidents in this date range.</p>
            ) : (
              <div className="space-y-3">
                {filteredIncidents.slice(0, 50).map(i => (
                  <div key={i.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-lg">{i.fields.Location}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        i.fields.Priority === 'High' ? 'bg-red-100 text-red-800' :
                        i.fields.Priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>{i.fields.Priority} Priority</span>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">{i.fields.Description}</p>
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>Guard: {i.fields.Guard}</span>
                      <span>{formatDateTime(i.fields['Date/Time'])}</span>
                    </div>
                  </div>
                ))}
                {filteredIncidents.length > 50 && (
                  <p className="text-sm text-gray-400 text-center">Showing 50 of {filteredIncidents.length} records</p>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'perimeter' && (
          <div>
            <h2 className="text-2xl font-bold mb-6">Perimeter Checks — {rangeLabel}</h2>
            {filteredChecks.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No perimeter checks in this date range.</p>
            ) : (
              <div className="space-y-3">
                {filteredChecks.slice(0, 50).map(c => (
                  <div key={c.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold">Check #{c.fields['Check ID'] || c.id.slice(-4)}</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        c.fields['Completion Status'] === 'Complete' ? 'bg-green-100 text-green-800' :
                        c.fields['Completion Status'] === 'Issue Found' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>{c.fields['Completion Status']}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>Officer: {c.fields['Security Guard Name']}</div>
                      <div>Time: {formatDateTime(c.fields['Check Time'])}</div>
                    </div>
                    {c.fields.Notes && (
                      <div className="mt-2 bg-gray-50 rounded p-2 text-sm"><strong>Notes:</strong> {c.fields.Notes}</div>
                    )}
                  </div>
                ))}
                {filteredChecks.length > 50 && (
                  <p className="text-sm text-gray-400 text-center">Showing 50 of {filteredChecks.length} records</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
